import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── HRIS Employee Management ───────────────────────────────
 * Full employee master records with Philippine tax profiles.
 * CRUD: Create, Read, Update, Delete.
 *
 * Uses as any pattern for flexibility with the repository layer
 * to support both Employee type and legacy employee records.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { employeeRepo } from '../../lib/db'
import {
  EMPLOYMENT_STATUS_LABELS, TAX_STATUS_LABELS,
} from '@repo/entity-hris'
import {
  Plus, Search, UserPlus, Pencil, Trash2,
} from 'lucide-react'

const STATUS_COLORS: Record<string, string> = {
  probationary: 'yellow', regular: 'green', contractual: 'blue',
  project_based: 'purple', seasonal: 'blue', resigned: 'red',
  terminated: 'red', retired: 'gray',
}

export function HrisEmployeePage() {
  const [loading, setLoading] = useState(true)
  const [employees, setEmployees] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    firstName: '', lastName: '', employeeCode: '',
    department: '', position: '', basicSalary: 0,
    employmentStatus: 'probationary',
    taxStatus: 'ME',
    phone: '', email: '', address: '',
    tin: '', sssNumber: '', philhealthNumber: '', pagibigNumber: '',
  })

  const loadEmployees = useCallback(async () => {
    setLoading(true)
    try {
      const res = await employeeRepo.findMany({ page: 1, pageSize: 500 })
      setEmployees('items' in res ? res.items : [])
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadEmployees() }, [loadEmployees])

  const resetForm = () => {
    setForm({ firstName: '', lastName: '', employeeCode: '', department: '', position: '',
      basicSalary: 0, employmentStatus: 'probationary', taxStatus: 'ME',
      phone: '', email: '', address: '', tin: '', sssNumber: '', philhealthNumber: '', pagibigNumber: '' })
    setEditId(null)
  }

  async function handleSave() {
    setSaving(true)
    try {
      const payload: any = {
        name: `${form.firstName} ${form.lastName}`.trim(),
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        role: form.position,
        baseSalary: Number(form.basicSalary),
        status: form.employmentStatus === 'regular' ? 'active' : form.employmentStatus,
        sex: 'male', nationality: 'Filipino',
        civilStatus: 'single', dateOfBirth: '1990-01-01',
        payFrequency: 'semi_monthly', basicSalary: Number(form.basicSalary),
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
        employeeCode: form.employeeCode, department: form.department,
        position: form.position, employmentStatus: form.employmentStatus,
        taxStatus: form.taxStatus, phone: form.phone, email: form.email,
        address: form.address, tin: form.tin, sssNumber: form.sssNumber,
        philhealthNumber: form.philhealthNumber, pagibigNumber: form.pagibigNumber,
      }

      if (editId) {
        await employeeRepo.update(editId, { ...payload, updatedBy: 'admin' })
      } else {
        await employeeRepo.create(payload)
      }
      setShowCreate(false)
      resetForm()
      await loadEmployees()
    } catch (e: any) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setSaving(false) }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this employee?')) return
    try { await employeeRepo.delete(id); await loadEmployees() }
    catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
  }

  function startEdit(emp: any) {
    const names = (emp.fullName ?? emp.name ?? '').split(' ')
    setForm({
      firstName: names[0] ?? '', lastName: names.slice(1).join(' ') ?? '',
      employeeCode: emp.employeeCode ?? '', department: emp.department ?? '',
      position: emp.position ?? emp.role ?? '', basicSalary: emp.basicSalary ?? emp.baseSalary ?? 0,
      employmentStatus: emp.employmentStatus ?? 'probationary',
      taxStatus: emp.taxStatus ?? 'ME',
      phone: emp.phone ?? '', email: emp.email ?? '', address: emp.address ?? '',
      tin: emp.tin ?? '', sssNumber: emp.sssNumber ?? '',
      philhealthNumber: emp.philhealthNumber ?? '', pagibigNumber: emp.pagibigNumber ?? '',
    })
    setEditId(emp.id)
    setShowCreate(true)
  }

  const filtered = employees.filter((e: any) => {
    const q = search.toLowerCase()
    const name = (e.fullName ?? e.name ?? '').toLowerCase()
    const dept = (e.department ?? '').toLowerCase()
    const pos = (e.position ?? e.role ?? '').toLowerCase()
    return name.includes(q) || dept.includes(q) || pos.includes(q)
  })

  const byDept = new Map<string, number>()
  filtered.forEach((e: any) => { const d = e.department ?? 'Unassigned'; byDept.set(d, (byDept.get(d) ?? 0) + 1) })

  return (
    <div className="p-6">
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Total Employees', filtered.length, 'blue'],
          ['Active', filtered.filter((e: any) => e.employmentStatus === 'regular').length, 'green'],
          ['Departments', byDept.size, 'purple'],
          ['Avg Salary', filtered.length > 0 ? `₱${Math.round(filtered.reduce((s: number, e: any) => s + ((e as any).basicSalary ?? 0), 0) / filtered.length).toLocaleString()}` : '₱0', 'amber'],
        ].map(([label, value, color]) => (
          <div key={label as string} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <p className={`text-xs text-${color}-600`}>{label as string}</p>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value as any}</p>
          </div>
        ))}
      </div>

      {/* Search + Actions */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search employees..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm" />
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true) }} icon={<UserPlus className="h-4 w-4" />}>
          Add Employee
        </Button>
      </div>

      {/* Employees Table */}
      <Card>
        <CardHeader title="Employees" description={`${filtered.length} records`} />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Code', 'Name', 'Department', 'Position', 'Salary', 'Status', 'Tax', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">No employees. Add one to get started.</td></tr>
              ) : filtered.map((emp: any) => (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-mono text-xs text-gray-500">{emp.employeeCode ?? '—'}</td>
                  <td className="px-3 py-2.5 font-medium text-gray-900">{emp.fullName ?? emp.name}</td>
                  <td className="px-3 py-2.5 text-gray-500">{emp.department ?? '—'}</td>
                  <td className="px-3 py-2.5 text-gray-600">{emp.position ?? emp.role ?? '—'}</td>
                  <td className="px-3 py-2.5 text-right font-medium">₱{(emp.basicSalary ?? emp.baseSalary ?? 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5">
                    <Badge color={(STATUS_COLORS[emp.employmentStatus ?? ''] ?? 'gray') as any}>
                      {EMPLOYMENT_STATUS_LABELS[emp.employmentStatus as keyof typeof EMPLOYMENT_STATUS_LABELS] ?? emp.employmentStatus}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{emp.taxStatus ?? '—'}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(emp)} className="rounded p-1 hover:bg-gray-100" title="Edit">
                        <Pencil className="h-3.5 w-3.5 text-blue-600" />
                      </button>
                      <button onClick={() => handleDelete(emp.id)} className="rounded p-1 hover:bg-gray-100" title="Delete">
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); resetForm() }}
        title={editId ? 'Edit Employee' : 'Add Employee'} size="lg"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowCreate(false); resetForm() }}>Cancel</Button>
            <Button onClick={handleSave} loading={saving} icon={<UserPlus className="h-4 w-4" />}>
              {editId ? 'Update' : 'Create'} Employee
            </Button>
          </div>
        }>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="First Name" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
          <Input label="Last Name" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required />
          <Input label="Employee Code" value={form.employeeCode} onChange={e => setForm({ ...form, employeeCode: e.target.value })} />
          <Input label="Department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} />
          <Input label="Position" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} />
          <Input label="Basic Salary (₱)" type="number" value={String(form.basicSalary)}
            onChange={e => setForm({ ...form, basicSalary: Number(e.target.value) })} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
            <select value={form.employmentStatus} onChange={e => setForm({ ...form, employmentStatus: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(EMPLOYMENT_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v as string}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Status</label>
            <select value={form.taxStatus} onChange={e => setForm({ ...form, taxStatus: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(TAX_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v as string}</option>
              ))}
            </select>
          </div>
          <Input label="TIN" value={form.tin} onChange={e => setForm({ ...form, tin: e.target.value })} placeholder="123-456-789-000" />
          <Input label="SSS Number" value={form.sssNumber} onChange={e => setForm({ ...form, sssNumber: e.target.value })} placeholder="02-1234567-8" />
          <Input label="PhilHealth Number" value={form.philhealthNumber} onChange={e => setForm({ ...form, philhealthNumber: e.target.value })} />
          <Input label="Pag-IBIG Number" value={form.pagibigNumber} onChange={e => setForm({ ...form, pagibigNumber: e.target.value })} />
          <Input label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          <Input label="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <div className="sm:col-span-2">
            <Input label="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  )
}
